import { useMemo } from 'react';
import { CheckPicker, SelectPicker } from 'rsuite';
import { FilterGroup, FilterLabel, FilterSeparator } from '../../../../../../components/Filters';
import {
  searchCategoriasCatalogo,
  searchCidadesCatalogo,
  searchProdutosCatalogo,
  searchRedesCatalogo,
  searchSecoesCatalogo,
  searchUfsCatalogo,
} from '../../filterServices';
import { useAsyncFilterOptions } from '../../hooks/useAsyncFilterOptions';
import { WARNING_PRODUTO, WARNING_VAREJO } from '../../monitoringWarningMessages';
import { notify } from '../../notify';
import { MonitoramentoPeriodo, MonitoramentoTipo } from '../../types';
import styles from './DetalhamentoMonitoramentoForm.module.scss';

const MAX_CONCORRENTES = 10;

const LOCAL_TIPO_OPTIONS = [
  { label: 'Cidade', value: 'CIDADE' },
  { label: 'UF', value: 'UF' },
  { label: 'Todo o território', value: 'TODO_TERRITORIO' },
];
// "Todo o sortimento" fica de fora por enquanto, mesmo motivo da Análise de Competitividade
// (AnaliseCompetitividade/utils.js): o backend só aceita PRODUTO/CATEGORIA/SECAO.
const TIPO_COMPARACAO_OPTIONS = [
  { label: 'Produto', value: 'PRODUTO' },
  { label: 'Categoria', value: 'CATEGORIA' },
  { label: 'Seção', value: 'SECAO' },
];
const PERIODO_OPTIONS = [
  { label: 'Últimas 4 semanas', value: MonitoramentoPeriodo.ULTIMAS_4_SEMANAS },
  { label: 'Últimas 8 semanas', value: MonitoramentoPeriodo.ULTIMAS_8_SEMANAS },
  { label: 'Últimas 12 semanas', value: MonitoramentoPeriodo.ULTIMAS_12_SEMANAS },
];

const CountLabel = ({ label, count }: { label: string; count: number }) => (
  <>
    {label} <span className={styles.countBadge}>{count}</span>
  </>
);

const renderCountLabel = (label: string) =>
  function (selected: string[]) {
    return <CountLabel label={label} count={selected.length} />;
  };

export type LocalMonitoramentoTipo = 'CIDADE' | 'UF' | 'TODO_TERRITORIO';

export type DetalhamentoMonitoramentoProduto = {
  produtoNome: string;
  produtoEan: string;
};

export type DetalhamentoMonitoramentoRede = {
  redeNome: string;
};

export type DetalhamentoMonitoramentoCidade = {
  cidadeNome: string;
  uf: string;
};

export type TipoComparacaoMonitoramento = 'PRODUTO' | 'CATEGORIA' | 'SECAO';

export type DetalhamentoMonitoramentoValue = {
  produtos: DetalhamentoMonitoramentoProduto[];
  // categorias/secoes só existem no modo varejo (!isProdutoMode), assim como tipoComparacao
  // só é lido nesse modo — mesma convivência que redeReferencia/redesConcorrentes já têm hoje.
  tipoComparacao: TipoComparacaoMonitoramento;
  categorias: string[];
  secoes: string[];
  redeReferencia: DetalhamentoMonitoramentoRede | null;
  redesConcorrentes: DetalhamentoMonitoramentoRede[];
  localTipo: LocalMonitoramentoTipo;
  cidades: DetalhamentoMonitoramentoCidade[];
  ufs: string[];
  periodo: MonitoramentoPeriodo;
};

type DetalhamentoMonitoramentoFormProps = {
  monitoringType: string | null;
  value: DetalhamentoMonitoramentoValue;
  onChange: (value: DetalhamentoMonitoramentoValue) => void;
};

export const DetalhamentoMonitoramentoForm = ({ monitoringType, value, onChange }: DetalhamentoMonitoramentoFormProps) => {
  const isProdutoMode = monitoringType === MonitoramentoTipo.COMPETITIVIDADE_PRODUTO;

  const produtoOptions = useAsyncFilterOptions('detalhamento-produtos', searchProdutosCatalogo);
  const redeOptions = useAsyncFilterOptions('detalhamento-redes', searchRedesCatalogo);
  const cidadeOptions = useAsyncFilterOptions('detalhamento-cidades', searchCidadesCatalogo);
  const ufOptions = useAsyncFilterOptions('detalhamento-ufs', searchUfsCatalogo);
  const categoriaOptions = useAsyncFilterOptions('detalhamento-categorias', searchCategoriasCatalogo);
  const secaoOptions = useAsyncFilterOptions('detalhamento-secoes', searchSecoesCatalogo);

  const produtoPickerData = useMemo(() => {
    const selecionadosForaDaBusca = value.produtos
      .filter(produto => !produtoOptions.options.some(option => option.value === produto.produtoEan))
      .map(produto => ({ label: `${produto.produtoEan} - ${produto.produtoNome}`, value: produto.produtoEan, ...produto }));
    return [...produtoOptions.options, ...selecionadosForaDaBusca];
  }, [produtoOptions.options, value.produtos]);

  const redePickerData = useMemo(() => {
    const redesSelecionadas = value.redeReferencia ? [value.redeReferencia, ...value.redesConcorrentes] : value.redesConcorrentes;
    const selecionadasForaDaBusca = redesSelecionadas
      .filter(rede => !redeOptions.options.some(option => option.value === rede.redeNome))
      .map(rede => ({ label: rede.redeNome, value: rede.redeNome, ...rede }));
    return [...redeOptions.options, ...selecionadasForaDaBusca];
  }, [redeOptions.options, value.redeReferencia, value.redesConcorrentes]);

  const cidadePickerData = useMemo(() => {
    const selecionadasForaDaBusca = value.cidades
      .filter(cidade => !cidadeOptions.options.some(option => option.value === cidade.cidadeNome))
      .map(cidade => ({ label: cidade.cidadeNome, value: cidade.cidadeNome, ...cidade }));
    return [...cidadeOptions.options, ...selecionadasForaDaBusca];
  }, [cidadeOptions.options, value.cidades]);

  const ufPickerData = useMemo(() => {
    const selecionadasForaDaBusca = value.ufs
      .filter(uf => !ufOptions.options.some(option => option.value === uf))
      .map(uf => ({ label: uf.replace(/_/g, ' '), value: uf }));
    return [...ufOptions.options, ...selecionadasForaDaBusca];
  }, [ufOptions.options, value.ufs]);

  const categoriaPickerData = useMemo(() => {
    const selecionadasForaDaBusca = value.categorias
      .filter(categoria => !categoriaOptions.options.some(option => option.value === categoria))
      .map(categoria => ({ label: categoria, value: categoria }));
    return [...categoriaOptions.options, ...selecionadasForaDaBusca];
  }, [categoriaOptions.options, value.categorias]);

  const secaoPickerData = useMemo(() => {
    const selecionadasForaDaBusca = value.secoes
      .filter(secao => !secaoOptions.options.some(option => option.value === secao))
      .map(secao => ({ label: secao, value: secao }));
    return [...secaoOptions.options, ...selecionadasForaDaBusca];
  }, [secaoOptions.options, value.secoes]);

  const redesFlatValue = value.redeReferencia
    ? [value.redeReferencia.redeNome, ...value.redesConcorrentes.map(rede => rede.redeNome)]
    : value.redesConcorrentes.map(rede => rede.redeNome);

  const findProduto = (ean: string) => {
    const existing = value.produtos.find(produto => produto.produtoEan === ean);
    if (existing) return existing;
    const option = produtoOptions.options.find(item => item.value === ean);
    return { produtoEan: ean, produtoNome: option?.produtoNome ?? '' };
  };

  const handleChangeProdutosFlat = (nextEans: string[]) => {
    onChange({ ...value, produtos: nextEans.map(findProduto) });
  };

  // Zera as três listas de escopo incondicionalmente a cada troca (mesma regra da Análise de
  // Competitividade, FiltersArea/index.js:handleSelectScopeType) — nunca reaproveita um handler de
  // outro campo pra limpar, pra não arrastar resíduo de um escopo pro outro.
  const handleChangeTipoComparacao = (next: TipoComparacaoMonitoramento) => {
    onChange({ ...value, tipoComparacao: next, produtos: [], categorias: [], secoes: [] });
  };

  const handleChangeCategorias = (nextCategorias: string[]) => {
    if (nextCategorias.length > MAX_CONCORRENTES) {
      notify('error', 4000, `Você pode selecionar no máximo ${MAX_CONCORRENTES} categorias.`);
      return;
    }
    onChange({ ...value, categorias: nextCategorias });
  };

  const handleChangeSecoes = (nextSecoes: string[]) => {
    if (nextSecoes.length > MAX_CONCORRENTES) {
      notify('error', 4000, `Você pode selecionar no máximo ${MAX_CONCORRENTES} seções.`);
      return;
    }
    onChange({ ...value, secoes: nextSecoes });
  };

  const handleChangeProdutoReferencia = (nextEan: string | null) => {
    const concorrentes = value.produtos.slice(1);
    onChange({ ...value, produtos: nextEan ? [findProduto(nextEan), ...concorrentes] : concorrentes });
  };

  const handleChangeProdutosConcorrentes = (nextEans: string[]) => {
    if (nextEans.length > MAX_CONCORRENTES) {
      notify('error', 4000, `Você pode selecionar no máximo ${MAX_CONCORRENTES} produtos concorrentes.`);
      return;
    }
    const referencia = value.produtos[0];
    onChange({ ...value, produtos: referencia ? [referencia, ...nextEans.map(findProduto)] : nextEans.map(findProduto) });
  };

  const handleChangeRedeReferencia = (nextNome: string | null) => {
    onChange({ ...value, redeReferencia: nextNome ? { redeNome: nextNome } : null });
  };

  const handleChangeRedesConcorrentes = (nextNomes: string[]) => {
    if (nextNomes.length > MAX_CONCORRENTES) {
      notify('error', 4000, `Você pode selecionar no máximo ${MAX_CONCORRENTES} redes concorrentes.`);
      return;
    }
    onChange({ ...value, redesConcorrentes: nextNomes.map(nome => ({ redeNome: nome })) });
  };

  const handleChangeRedesFlat = (nextNomes: string[]) => {
    if (nextNomes.length > MAX_CONCORRENTES) {
      notify('error', 4000, `Você pode selecionar no máximo ${MAX_CONCORRENTES} redes.`);
      return;
    }
    const [redeReferenciaNome, ...redesConcorrentesNomes] = nextNomes;
    onChange({
      ...value,
      redeReferencia: redeReferenciaNome ? { redeNome: redeReferenciaNome } : null,
      redesConcorrentes: redesConcorrentesNomes.map(nome => ({ redeNome: nome })),
    });
  };

  const handleChangeLocalTipo = (nextTipo: LocalMonitoramentoTipo) => {
    onChange({ ...value, localTipo: nextTipo, cidades: [], ufs: [] });
  };

  const handleChangeCidades = (nextNomes: string[]) => {
    // O catálogo (painel_geral) só retorna o nome da cidade, sem UF — até o back confirmar como
    // resolver isso, o uf vai vazio (ainda não confirmado com o back).
    onChange({ ...value, cidades: nextNomes.map(nome => ({ cidadeNome: nome, uf: '' })) });
  };

  const handleChangeUfs = (nextUfs: string[]) => {
    onChange({ ...value, ufs: nextUfs });
  };

  const handleChangePeriodo = (nextPeriodo: MonitoramentoPeriodo) => {
    onChange({ ...value, periodo: nextPeriodo });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Detalhamento do monitoramento</h2>
        <p className={styles.subtitle}>Selecione os detalhes que irão guiar o seu alerta</p>
      </div>
      <div className={styles.warningBox}>{isProdutoMode ? WARNING_PRODUTO : WARNING_VAREJO}</div>

      <div className={styles.chainedFiltersBox}>
        <FilterGroup className={styles.filterRow}>
          <FilterLabel>Monitorar</FilterLabel>
          {isProdutoMode ? (
            <div className={styles.tipoPillStatic}>Produtos</div>
          ) : (
            <SelectPicker
              className={styles.tipoPill}
              data={TIPO_COMPARACAO_OPTIONS}
              value={value.tipoComparacao}
              cleanable={false}
              searchable={false}
              onChange={handleChangeTipoComparacao}
            />
          )}
          <FilterSeparator />
          {isProdutoMode ? (
            <>
              <SelectPicker
                className={styles.pill}
                data={produtoPickerData}
                value={value.produtos[0]?.produtoEan ?? null}
                placeholder="Produto referência"
                loading={produtoOptions.isLoading}
                onSearch={produtoOptions.onSearch}
                onChange={handleChangeProdutoReferencia}
                locale={{ searchPlaceholder: 'Buscar produto', noResultsText: 'Digite para buscar' }}
              />
              <span className={styles.vsText}>Vs</span>
              <CheckPicker
                className={styles.pill}
                data={produtoPickerData}
                value={value.produtos.slice(1).map(produto => produto.produtoEan)}
                placeholder="Escolha até 10 produtos"
                loading={produtoOptions.isLoading}
                onSearch={produtoOptions.onSearch}
                onChange={handleChangeProdutosConcorrentes}
                renderValue={renderCountLabel('Produtos competidores')}
                locale={{ searchPlaceholder: 'Buscar produto', noResultsText: 'Digite para buscar' }}
              />
            </>
          ) : value.tipoComparacao === 'CATEGORIA' ? (
            <CheckPicker
              className={styles.pill}
              data={categoriaPickerData}
              value={value.categorias}
              placeholder="Escolha até 10 categorias"
              loading={categoriaOptions.isLoading}
              onSearch={categoriaOptions.onSearch}
              onChange={handleChangeCategorias}
              renderValue={renderCountLabel('Categorias selecionadas')}
              locale={{ searchPlaceholder: 'Buscar categoria', noResultsText: 'Digite para buscar' }}
            />
          ) : value.tipoComparacao === 'SECAO' ? (
            <CheckPicker
              className={styles.pill}
              data={secaoPickerData}
              value={value.secoes}
              placeholder="Escolha até 10 seções"
              loading={secaoOptions.isLoading}
              onSearch={secaoOptions.onSearch}
              onChange={handleChangeSecoes}
              renderValue={renderCountLabel('Seções selecionadas')}
              locale={{ searchPlaceholder: 'Buscar seção', noResultsText: 'Digite para buscar' }}
            />
          ) : (
            <CheckPicker
              className={styles.pill}
              data={produtoPickerData}
              value={value.produtos.map(produto => produto.produtoEan)}
              placeholder="Escolha os produtos"
              loading={produtoOptions.isLoading}
              onSearch={produtoOptions.onSearch}
              onChange={handleChangeProdutosFlat}
              renderValue={renderCountLabel('Produtos selecionados')}
              locale={{ searchPlaceholder: 'Buscar produto', noResultsText: 'Digite para buscar' }}
            />
          )}
        </FilterGroup>

        <FilterGroup className={styles.filterRow}>
          <FilterLabel>Entre</FilterLabel>
          <div className={styles.tipoPillStatic}>Redes</div>
          <FilterSeparator />
          {isProdutoMode ? (
            <CheckPicker
              className={styles.pill}
              data={redePickerData}
              value={redesFlatValue}
              placeholder="Escolha as redes"
              loading={redeOptions.isLoading}
              onSearch={redeOptions.onSearch}
              onChange={handleChangeRedesFlat}
              renderValue={renderCountLabel('Redes selecionadas')}
              locale={{ searchPlaceholder: 'Buscar rede', noResultsText: 'Digite para buscar' }}
            />
          ) : (
            <>
              <SelectPicker
                className={styles.pill}
                data={redePickerData}
                value={value.redeReferencia?.redeNome ?? null}
                placeholder="Rede referência"
                loading={redeOptions.isLoading}
                onSearch={redeOptions.onSearch}
                onChange={handleChangeRedeReferencia}
                locale={{ searchPlaceholder: 'Buscar rede', noResultsText: 'Digite para buscar' }}
              />
              <span className={styles.vsText}>Vs</span>
              <CheckPicker
                className={styles.pill}
                data={redePickerData}
                value={value.redesConcorrentes.map(rede => rede.redeNome)}
                placeholder="Escolha até 10 redes"
                loading={redeOptions.isLoading}
                onSearch={redeOptions.onSearch}
                onChange={handleChangeRedesConcorrentes}
                renderValue={renderCountLabel('Redes concorrentes')}
                locale={{ searchPlaceholder: 'Buscar rede', noResultsText: 'Digite para buscar' }}
              />
            </>
          )}
        </FilterGroup>

        <FilterGroup className={styles.filterRow}>
          <FilterLabel>Local</FilterLabel>
          <SelectPicker
            className={styles.tipoPill}
            data={LOCAL_TIPO_OPTIONS}
            value={value.localTipo}
            cleanable={false}
            searchable={false}
            onChange={handleChangeLocalTipo}
          />
          {value.localTipo !== 'TODO_TERRITORIO' && <FilterSeparator />}
          {value.localTipo === 'UF' && (
            <CheckPicker
              className={styles.pill}
              data={ufPickerData}
              value={value.ufs}
              placeholder="Escolha as UFs"
              loading={ufOptions.isLoading}
              onSearch={ufOptions.onSearch}
              onChange={handleChangeUfs}
              renderValue={renderCountLabel('UF selecionadas')}
              locale={{ searchPlaceholder: 'Buscar UF', noResultsText: 'Digite para buscar' }}
            />
          )}
          {value.localTipo === 'CIDADE' && (
            <CheckPicker
              className={styles.pill}
              data={cidadePickerData}
              value={value.cidades.map(cidade => cidade.cidadeNome)}
              placeholder="Escolha as cidades"
              loading={cidadeOptions.isLoading}
              onSearch={cidadeOptions.onSearch}
              onChange={handleChangeCidades}
              renderValue={renderCountLabel('Cidades selecionadas')}
              locale={{ searchPlaceholder: 'Buscar cidade', noResultsText: 'Digite para buscar' }}
            />
          )}
        </FilterGroup>

        <FilterGroup className={styles.filterRow}>
          <FilterLabel>Período</FilterLabel>
          <SelectPicker
            className={styles.tipoPill}
            data={PERIODO_OPTIONS}
            value={value.periodo}
            cleanable={false}
            searchable={false}
            onChange={handleChangePeriodo}
          />
        </FilterGroup>
      </div>
    </div>
  );
};
